import {useEffect, useState} from 'react';

/*
 * Chart colours, as values a chart library can actually use.
 *
 * Everything else in the app passes colours around as "var(--ds-...)" - see
 * Colors.js - and lets the browser resolve them at paint time, which is what
 * makes a theme change reach an inline style. Recharts cannot work that way: it
 * puts the colour into an SVG presentation attribute, where var() is not
 * reliable, and it reads the same string back to paint the legend swatch, where
 * it is not a colour at all. So the token is resolved here, once, into the hex
 * the theme currently holds.
 *
 * Resolving once would be a bug, though - the theme can change while the page
 * is open, and a chart painted from a stale palette keeps the light colours on
 * the dark ground. The observer below watches the one attribute Theme.js
 * touches and re-reads when it moves.
 */

const TOKENS = {
    series1: '--ds-chart-1',
    series2: '--ds-chart-2',
    series3: '--ds-chart-3',
    series4: '--ds-chart-4',
    series5: '--ds-chart-5',
    series6: '--ds-chart-6',

    webchat: '--ds-chart-webchat',
    messenger: '--ds-chart-messenger',
    telegram: '--ds-chart-telegram',
    instagram: '--ds-chart-instagram',
    whatsapp: '--ds-chart-whatsapp',
    viber: '--ds-chart-viber',
    webex: '--ds-chart-webex',

    statusAvailable: '--ds-chart-status-available',
    statusPause: '--ds-chart-status-pause',
    statusNotHere: '--ds-chart-status-not-here',
    statusOffline: '--ds-chart-status-offline',

    positive: '--ds-chart-positive',
    negative: '--ds-chart-negative',
    warning: '--ds-chart-warning',
    neutral: '--ds-chart-neutral',

    grid: '--ds-chart-grid',
    axis: '--ds-chart-axis',
};

/* what a colour falls back to when the stylesheet has not arrived yet, or when
   there is no document at all - a chart with grey bars beats one that throws */
const FALLBACK = '#9aa3b5';

const read = () => {
    const resolved = {};

    if (typeof window === 'undefined' || !window.getComputedStyle) {
        Object.keys(TOKENS).forEach(name => {
            resolved[name] = FALLBACK;
        });
        return resolved;
    }

    const styles = window.getComputedStyle(document.documentElement);

    Object.keys(TOKENS).forEach(name => {
        const value = styles.getPropertyValue(TOKENS[name]);
        resolved[name] = value && value.trim() ? value.trim() : FALLBACK;
    });

    return resolved;
};

/* the series colours in the order a chart should reach for them */
export const seriesOrder = ['series1', 'series2', 'series3', 'series4', 'series5', 'series6'];

/**
 * The resolved palette, kept in step with the theme.
 *
 * `palette.webchat` is "#4a6bd1" in the light theme and "#7b94e8" in the dark,
 * and the component re-renders when that changes.
 */
export default function useChartPalette() {
    const [palette, setPalette] = useState(read);

    useEffect(() => {
        if (typeof MutationObserver === 'undefined') return undefined;

        const observer = new MutationObserver(() => setPalette(read()));

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });

        return () => observer.disconnect();
    }, []);

    return palette;
}

/**
 * A colour per platform, falling back to the generic series when a platform we
 * have no colour for turns up - a new messenger is added to the tariff more
 * often than this file is edited.
 */
export function platformColor(palette, platform, index) {
    const key = String(platform || '').toLowerCase();
    if (palette[key]) return palette[key];
    return palette[seriesOrder[index % seriesOrder.length]];
}



// WEBPACK FOOTER //
// ./src/components-new/general/charts/palette.js